<script setup lang="ts">
import { computed, watch } from 'vue';
import { ui } from '../editor/bridge';
import { propsDockedFor, selectedObjects, setPropsDockedFor } from '../editor/selection';
import { modulePanels, moduleTypeLabel } from '../modules';
import { settings } from '../platform';
import { useApp } from '../stores/app';
import { t, typeLabel } from '../i18n';
import Icon from './Icon.vue';
import ModuleProps from './ModuleProps.vue';
import ModulePanelBody from './ModulePanelBody.vue';
import PropertiesBar from './PropertiesBar.vue';

/**
 * The right sidebar of the platform. It shows
 * - the properties of the selection, for any objects, when the user put them here instead of the
 *   top bar (`propsDockedFor`: the user's choice, or a module's default `propertiesIn`; the top bar is hidden then) — plus a module object's own fields;
 * - a module's panel (its button in the top-right bar), over the properties while it is open.
 */
const app = useApp();
const one = computed(() => (selectedObjects.value.length === 1 ? selectedObjects.value[0] : null));
const moduleObj = computed(() => (one.value && !one.value.type.startsWith('core:') ? one.value : null));
const panel = computed(() => (ui.sidebar?.kind === 'panel' ? modulePanels().find((p) => p.moduleId === ui.sidebar!.moduleId) ?? null : null));
const docked = computed(() => ui.selection.length > 0 && !ui.editingText && propsDockedFor(selectedObjects.value));
const title = computed(() => {
  if (panel.value) return panel.value.title;
  if (one.value) return one.value.name || typeLabel(one.value.type, moduleTypeLabel);
  return t('props.multiple', { n: selectedObjects.value.length });
});
const buttonHidden = computed(() => !!panel.value && settings.hiddenPanelButtons.includes(panel.value.moduleId));

// the module stopped or was removed: its panel goes away
watch(panel, (p) => {
  if (ui.sidebar?.kind === 'panel' && !p) ui.sidebar = null;
});

function toggleButton(): void {
  const id = panel.value?.moduleId;
  if (!id) return;
  settings.hiddenPanelButtons = buttonHidden.value ? settings.hiddenPanelButtons.filter((x) => x !== id) : [...settings.hiddenPanelButtons, id];
  app.notify('info', buttonHidden.value ? t('sidebar.buttonHidden') : t('sidebar.buttonShown'));
}

/** Back to the top bar. */
function undock(): void {
  setPropsDockedFor(selectedObjects.value, false);
}
</script>

<template>
  <aside v-if="panel || docked" class="float card right-sidebar" :class="{ low: ui.barBelow, 'with-chat': ui.chatOpen }" :aria-label="title" @pointerdown.stop @keydown.stop>
    <header>
      <strong>{{ title }}</strong>
      <span class="actions">
        <button
          v-if="panel"
          class="icon-btn"
          :class="{ active: !buttonHidden }"
          :aria-pressed="!buttonHidden"
          :aria-label="buttonHidden ? t('sidebar.showButton') : t('sidebar.hideButton')"
          :title="buttonHidden ? t('sidebar.showButton') : t('sidebar.hideButton')"
          data-act="toggle-button"
          @click="toggleButton"
        >
          <Icon name="apps" :size="14" />
        </button>
        <button v-else class="icon-btn" :aria-label="t('props.dockTop')" :title="t('props.dockTop')" data-act="dock-top" @click="undock"><Icon name="dockTop" :size="15" /></button>
        <button v-if="panel" class="icon-btn" :aria-label="t('common.close')" @click="ui.sidebar = null"><Icon name="close" :size="14" /></button>
      </span>
    </header>
    <div class="body" :class="{ framed: !!panel }">
      <ModulePanelBody v-if="panel" :key="panel.moduleId" :module-id="panel.moduleId" />
      <template v-else>
        <PropertiesBar docked />
        <ModuleProps v-if="moduleObj" :obj="moduleObj" />
      </template>
    </div>
  </aside>
</template>

<style scoped>
.right-sidebar {
  top: 64px;
  right: 12px;
  width: 372px;
  max-width: calc(100% - 24px);
  max-height: calc(100% - var(--bottom-reserve, 64px) - 76px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.right-sidebar.low {
  top: 116px;
  max-height: calc(100% - var(--bottom-reserve, 64px) - 128px);
}
/* the chat owns the right edge; this panel sits to its left while both fit */
@media (min-width: 960px) {
  .right-sidebar.with-chat {
    right: 372px;
    max-width: calc(100% - 400px);
  }
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 8px 4px 16px;
}
header strong {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.actions {
  display: flex;
  gap: 2px;
}
.body {
  overflow-y: auto;
  padding: 0 16px 12px;
}
/* a module panel has its own padding inside */
.body.framed {
  padding: 0 4px 8px;
}
</style>
